import { UserProfileDTO } from '@/api/auth';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { refreshDepartmentAccess, useAuth } from '@/stores/auth';
import { useNavigate } from '@tanstack/react-router';
import { clearInvalidProfile } from '@/helpers/invalidProfiles';
import { forgetInactivityProfile } from '@/helpers/inactivityProfile';
import { getProfileReturnPath } from '@/helpers/loginFlow';
import { useAccessToken } from '@/hooks/useAxiosAuth';

interface ChangeProfileOptions {
  reload?: boolean;
  returnTo?: string;
  // Restores "Visa som": where "Tillbaka" goes, and which page it opens.
  previousProfile?: UserProfileDTO;
  preImpersonationLocation?: string;
}

export const useChangeProfile = () => {
  const queryClient = useQueryClient();
  const { setUserProfile, setPreImpersonationLocation } = useAuth();
  const navigate = useNavigate();
  const getAccessToken = useAccessToken();

  return useCallback(
    async (
      profile: UserProfileDTO,
      {
        reload = false,
        returnTo,
        previousProfile,
        preImpersonationLocation,
      }: ChangeProfileOptions = {},
    ) => {
      // Clear the query cache first to prevent stale data
      queryClient.resetQueries();
      queryClient.clear();

      // Set new profile (clears accessToken and permissions)
      setUserProfile(profile, previousProfile);
      if (preImpersonationLocation) {
        setPreImpersonationLocation(preImpersonationLocation);
      }

      clearInvalidProfile(profile.id);
      forgetInactivityProfile();

      // Small delay to let state updates propagate through Zustand persist middleware
      await new Promise((resolve) => setTimeout(resolve, 10));

      const returnPath = getProfileReturnPath(
        returnTo,
        window.location.origin,
        profile.tenant_id,
      );

      if (returnPath) {
        // Route guards read permissions and department access synchronously,
        // so load them before navigating or the guard bounces to the dashboard.
        try {
          await getAccessToken();
        } catch {
          // getAccessToken already redirected to login.
          return;
        }
        if (profile.organization_id) {
          await refreshDepartmentAccess();
        }
        await navigate({ href: returnPath, replace: true });
      } else {
        // Navigate to new tenant
        await navigate({
          to: '/$tenantId',
          params: { tenantId: profile.tenant_id },
        });
      }

      if (reload) {
        // Invalidate queries to refetch with new profile
        queryClient.invalidateQueries();
      }
    },
    [
      queryClient,
      setUserProfile,
      setPreImpersonationLocation,
      navigate,
      getAccessToken,
    ],
  );
};
